import * as THREE from "three";
import { defineModel, type Part } from "../../lib";

// Pip — a tiny round buddy robot, the little sibling of "sprocket" (same amber
// shell, gunmetal joints and glowing cyan lens): a ball wheel for a base, a
// hovering capsule body on a stubby suspension fork, one big friendly cyclops
// eye and a whippy antenna with a warning-orange bobble. About 0.5 m tall and
// built to be adorable — everything about it is round.
//
// Frame: faces +Z (where the eye looks), sits on the floor (y = 0).
//
// Rig layout (flat, no hierarchy — each named node moves about its own pivot):
//   - "body"    : the capsule shell + suspension fork + arm nubs. Hover-bobs
//                 over the ball wheel; pivot at its underside.
//   - "head"    : the dome with the cyclops eye. Pivot where it seats on the
//                 body so nods and spins read right.
//   - "antenna" : the whip + bobble on the crown, on the spin axis so the
//                 excited spin stays seamless. Pivot at its root.
// The ball wheel and its axle stay planted (unnamed, merged into the static
// mesh) — Pip rolls in spirit, not in geometry.

const BODY = "#e0a32e";       // warm amber shell (the family colour)
const BODY_DARK = "#bd861d";  // shaded belly band
const WHEEL = "#33343a";      // rubber ball wheel
const METAL = "#9aa0a6";      // fork, axle, arm nubs, antenna whip
const EYE = "#2c2f36";        // eye housing + chest screen
const LENS = "#79d6ea";       // the glowing cyan lens
const SPARK = "#d9f3fa";      // lens highlight
const ACCENT = "#e8542b";     // warning-orange antenna bobble

// ── frame heights ────────────────────────────────────────────────────────────
const WHEEL_R = 0.085;         // ball wheel radius (centre sits at this height)
const BODY_CY = 0.30;          // capsule body centre (hovers over the wheel)
const BODY_BOTTOM = 0.185;     // capsule underside — the body pivots here
const HEAD_CY = 0.44;          // head dome centre (r 0.08)
const HEAD_SEAT = 0.415;       // where the dome seats on the body — head pivot
const CROWN_Y = 0.515;         // head crown — the antenna roots here

const BODY_PIVOT: [number, number, number] = [0, BODY_BOTTOM, 0];
const HEAD_PIVOT: [number, number, number] = [0, HEAD_SEAT, 0];
const ANTENNA_PIVOT: [number, number, number] = [0, CROWN_Y, 0];

function pipParts(): Part[] {
  return [
    // ── the ball wheel (static): a rubber sphere on a stub axle ─────────────
    { geometry: new THREE.SphereGeometry(WHEEL_R, 16, 12), color: WHEEL,
      position: [0, WHEEL_R, 0] },
    { geometry: new THREE.CylinderGeometry(0.02, 0.02, 0.19, 10), color: METAL,
      position: [0, WHEEL_R, 0], rotation: [0, 0, Math.PI / 2] },

    // ── the hovering body: capsule shell, belly band, fork, arm nubs ────────
    { geometry: new THREE.CapsuleGeometry(0.085, 0.06, 6, 14), color: BODY,
      position: [0, BODY_CY, 0], name: "body" },
    // A shaded belly band where the shell rounds under.
    { geometry: new THREE.CylinderGeometry(0.0865, 0.083, 0.05, 16), color: BODY_DARK,
      position: [0, 0.265, 0], name: "body" },
    // A tiny dark chest screen with a cyan blip — Pip's heartbeat.
    { geometry: new THREE.BoxGeometry(0.07, 0.05, 0.016), color: EYE,
      position: [0, 0.31, 0.078], name: "body" },
    { geometry: new THREE.BoxGeometry(0.028, 0.02, 0.01), color: LENS,
      position: [0, 0.31, 0.087], name: "body" },
    // The suspension fork: two prongs reaching down to straddle the wheel.
    { geometry: new THREE.BoxGeometry(0.028, 0.12, 0.055), color: METAL,
      position: [-0.07, 0.175, 0], name: "body" },
    { geometry: new THREE.BoxGeometry(0.028, 0.12, 0.055), color: METAL,
      position: [0.07, 0.175, 0], name: "body" },
    // Two little arm nubs — all the arms a buddy this small needs.
    { geometry: new THREE.SphereGeometry(0.032, 10, 8), color: METAL,
      position: [-0.098, 0.30, 0.01], name: "body" },
    { geometry: new THREE.SphereGeometry(0.032, 10, 8), color: METAL,
      position: [0.098, 0.30, 0.01], name: "body" },

    // ── the head dome with the big cyclops eye ──────────────────────────────
    { geometry: new THREE.SphereGeometry(0.08, 16, 12), color: BODY,
      position: [0, HEAD_CY, 0], name: "head" },
    // Eye housing: a dark forward-pointing ring, sprocket-family style.
    { geometry: new THREE.CylinderGeometry(0.054, 0.054, 0.05, 16), color: EYE,
      position: [0, 0.45, 0.055], rotation: [Math.PI / 2, 0, 0], name: "head" },
    // The big glowing lens, with a pupil dot and a sparkle for life.
    { geometry: new THREE.CylinderGeometry(0.044, 0.044, 0.018, 16), color: LENS,
      position: [0, 0.45, 0.082], rotation: [Math.PI / 2, 0, 0], name: "head" },
    { geometry: new THREE.CylinderGeometry(0.018, 0.018, 0.01, 10), color: EYE,
      position: [0, 0.453, 0.09], rotation: [Math.PI / 2, 0, 0], name: "head" },
    { geometry: new THREE.SphereGeometry(0.008, 6, 5), color: SPARK,
      position: [0.013, 0.465, 0.092], name: "head" },

    // ── the bouncy antenna on the crown (on the spin axis) ──────────────────
    { geometry: new THREE.CylinderGeometry(0.007, 0.009, 0.09, 6), color: METAL,
      position: [0, CROWN_Y + 0.04, 0], name: "antenna" },
    { geometry: new THREE.SphereGeometry(0.023, 10, 8), color: ACCENT,
      position: [0, CROWN_Y + 0.095, 0], name: "antenna" },
  ];
}

export const pip = defineModel({
  id: "pip",
  batchId: "mess",
  name: "Pip",
  tags: ["mess", "cute", "animated", "robot", "bot", "buddy", "mascot", "scifi"],
  summary: "A tiny round buddy robot on a ball wheel, with one big cyclops eye and a bouncy antenna.",
  description:
    "Pip is a tiny round buddy robot, little sibling of the salvage bot " +
    "Sprocket: an amber capsule body hover-bobbing on a suspension fork over a " +
    "rubber ball wheel, one big friendly cyclops eye with a glowing cyan lens, " +
    "a chest blip and a whippy antenna with an orange bobble. The body, head " +
    "and antenna are separately posable; it ships an idle hover-bob and an " +
    "excited spin-in-place with the antenna whipping out.",
  author: "models",
  footprint: 0.25,
  tiles: { w: 1, h: 1 },
  parts: pipParts,
  animations: [
    // 1. Idle — the hover-bob: the body floats up and down over the planted
    //    wheel, the head rides along with a touch more travel and a little
    //    nod, and the antenna sways lazily behind the beat.
    {
      id: "idle",
      label: "Hover bob",
      duration: 2.6,
      loop: true,
      tracks: [
        {
          target: "body", pivot: BODY_PIVOT,
          position: [
            { t: 0, value: [0, 0, 0], ease: "sine" },
            { t: 1.3, value: [0, 0.02, 0], ease: "sine" },
            { t: 2.6, value: [0, 0, 0], ease: "sine" },
          ],
        },
        {
          target: "head", pivot: HEAD_PIVOT,
          position: [
            { t: 0, value: [0, 0, 0], ease: "sine" },
            { t: 1.3, value: [0, 0.028, 0], ease: "sine" },
            { t: 2.6, value: [0, 0, 0], ease: "sine" },
          ],
          rotation: [
            { t: 0, value: [0.04, 0, 0], ease: "sine" },
            { t: 1.3, value: [-0.04, 0, 0], ease: "sine" },
            { t: 2.6, value: [0.04, 0, 0], ease: "sine" },
          ],
        },
        {
          target: "antenna", pivot: ANTENNA_PIVOT,
          position: [
            { t: 0, value: [0, 0, 0], ease: "sine" },
            { t: 1.5, value: [0, 0.03, 0], ease: "sine" },
            { t: 2.6, value: [0, 0, 0], ease: "sine" },
          ],
          rotation: [
            { t: 0, value: [0.08, 0, 0.06], ease: "sine" },
            { t: 1.3, value: [-0.1, 0, -0.06], ease: "sine" },
            { t: 2.6, value: [0.08, 0, 0.06], ease: "sine" },
          ],
        },
      ],
    },

    // 2. Spin — pure excitement: one full spin in place per cycle (body, head
    //    and antenna turn together about the wheel's axis) with two happy hops
    //    on the way round and the antenna whipping outward from the spin.
    {
      id: "spin",
      label: "Excited spin",
      duration: 1.6,
      loop: true,
      tracks: [
        {
          target: "body", pivot: BODY_PIVOT,
          rotation: [
            { t: 0, value: [0, 0, 0] },
            { t: 0.4, value: [0, Math.PI / 2, 0] },
            { t: 0.8, value: [0, Math.PI, 0] },
            { t: 1.2, value: [0, Math.PI * 1.5, 0] },
            { t: 1.6, value: [0, Math.PI * 2, 0] },
          ],
          position: [
            { t: 0, value: [0, 0, 0], ease: "sine" },
            { t: 0.4, value: [0, 0.035, 0], ease: "sine" },
            { t: 0.8, value: [0, 0, 0], ease: "sine" },
            { t: 1.2, value: [0, 0.035, 0], ease: "sine" },
            { t: 1.6, value: [0, 0, 0], ease: "sine" },
          ],
        },
        {
          target: "head", pivot: HEAD_PIVOT,
          rotation: [
            { t: 0, value: [0, 0, 0] },
            { t: 0.4, value: [0, Math.PI / 2, 0] },
            { t: 0.8, value: [0, Math.PI, 0] },
            { t: 1.2, value: [0, Math.PI * 1.5, 0] },
            { t: 1.6, value: [0, Math.PI * 2, 0] },
          ],
          position: [
            { t: 0, value: [0, 0, 0], ease: "sine" },
            { t: 0.4, value: [0, 0.042, 0], ease: "sine" },
            { t: 0.8, value: [0, 0, 0], ease: "sine" },
            { t: 1.2, value: [0, 0.042, 0], ease: "sine" },
            { t: 1.6, value: [0, 0, 0], ease: "sine" },
          ],
        },
        // The antenna spins with the head but tips over as it goes, so the
        // bobble whips a circle in the air (the z-tilt is applied before the
        // y-spin, so the lean direction travels around with the turn).
        {
          target: "antenna", pivot: ANTENNA_PIVOT,
          rotation: [
            { t: 0, value: [0, 0, -0.06] },
            { t: 0.4, value: [0, Math.PI / 2, -0.34] },
            { t: 0.8, value: [0, Math.PI, -0.4] },
            { t: 1.2, value: [0, Math.PI * 1.5, -0.34] },
            { t: 1.6, value: [0, Math.PI * 2, -0.06] },
          ],
          position: [
            { t: 0, value: [0, 0, 0], ease: "sine" },
            { t: 0.4, value: [0, 0.045, 0], ease: "sine" },
            { t: 0.8, value: [0, 0, 0], ease: "sine" },
            { t: 1.2, value: [0, 0.045, 0], ease: "sine" },
            { t: 1.6, value: [0, 0, 0], ease: "sine" },
          ],
        },
      ],
    },
  ],
});
